import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import verkkoLogo from './assets/verkko-logo.svg';
import verkkoLogoWhite from './assets/verkko-logo-white.svg';

// Custom hook for smooth scrolling to sections
const useScrollToSection = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (sectionId, updateUrl = true) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80; // Account for fixed header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      if (updateUrl) {
        navigate(`/${sectionId}`, { replace: true });
      }
    }
  };

  // Handle initial load and direct URL access
  useEffect(() => {
    const hash = location.pathname.slice(1); // Remove leading slash
    if (hash && ['about', 'process', 'services'].includes(hash)) {
      // Small delay to ensure DOM is ready
      setTimeout(() => scrollToSection(hash, false), 100);
    } else if (location.pathname !== '/') {
      // Handle unknown routes by redirecting to home
      navigate('/', { replace: true });
    }
  }, [location.pathname, navigate]);

  return scrollToSection;
};

const MainContent = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    phone: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
  const [isModalOpen, setIsModalOpen] = useState(false);

  const scrollToSection = useScrollToSection();

  // Handle domain redirect from verkko.com.au to verkko.au
  useEffect(() => {
    if (window.location.hostname === 'verkko.com.au') {
      const currentPath = window.location.pathname + window.location.search + window.location.hash;
      window.location.replace(`https://verkko.au${currentPath}`);
      return;
    }
  }, []);

  // Input sanitization function to prevent XSS
  const sanitizeInput = (input, shouldTrim = false) => {
    if (typeof input !== 'string') return '';
    let sanitized = input
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove script tags
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/javascript:/gi, '') // Remove javascript: protocol
      .replace(/on\w+\s*=/gi, ''); // Remove event handlers
    
    return shouldTrim ? sanitized.trim() : sanitized;
  };

  // Email validation regex
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate individual field
  const validateField = (name, value) => {
    const sanitizedValue = sanitizeInput(value, false); // Don't trim while typing
    const trimmedValue = sanitizedValue.trim(); // Only trim for validation
    const errors = {};

    switch (name) {
      case 'name':
        if (!trimmedValue) {
          errors.name = 'Name is required';
        } else if (trimmedValue.length < 2) {
          errors.name = 'Name must be at least 2 characters';
        } else if (sanitizedValue.length > 100) {
          errors.name = 'Name must be less than 100 characters';
        }
        break;
      
      case 'email':
        if (!trimmedValue) {
          errors.email = 'Email is required';
        } else if (!isValidEmail(trimmedValue)) {
          errors.email = 'Please enter a valid email address';
        } else if (sanitizedValue.length > 254) {
          errors.email = 'Email must be less than 254 characters';
        }
        break;
      
      case 'company':
        if (sanitizedValue.length > 100) {
          errors.company = 'Company name must be less than 100 characters';
        }
        break;
      
      case 'phone':
        if (sanitizedValue.length > 20) {
          errors.phone = 'Phone number must be less than 20 characters';
        }
        break;
      
      case 'message':
        if (!trimmedValue) {
          errors.message = 'Message is required';
        } else if (trimmedValue.length < 10) {
          errors.message = 'Message must be at least 10 characters';
        } else if (sanitizedValue.length > 1000) {
          errors.message = 'Message must be less than 1000 characters';
        }
        break;
      
      default:
        break;
    }

    return { sanitizedValue, errors };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const { sanitizedValue, errors } = validateField(name, value);
    
    setFormData(prevData => ({
      ...prevData,
      [name]: sanitizedValue
    }));

    // Clear or set field-specific errors
    setFormErrors(prevErrors => ({
      ...prevErrors,
      [name]: errors[name] || null
    }));
  };

  const openModal = () => {
    setIsModalOpen(true);
    setSubmitStatus(null);
    setFormErrors({});
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSubmitStatus(null);
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Validate all fields
    const allErrors = {};
    let hasErrors = false;

    Object.keys(formData).forEach(fieldName => {
      const { errors } = validateField(fieldName, formData[fieldName]);
      if (errors[fieldName]) {
        allErrors[fieldName] = errors[fieldName];
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setFormErrors(allErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      // Create a trimmed version of the form data for submission
      const trimmedFormData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        company: formData.company.trim(),
        phone: formData.phone.trim(),
        message: formData.message.trim()
      };

      const response = await fetch('https://api.verkko.au/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(trimmedFormData),
      });

      const responseData = await response.json();

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          message: '',
          phone: ''
        });
        setFormErrors({});
        // Close modal after successful submission
        setTimeout(() => {
          closeModal();
        }, 2000);
      } else {
        throw new Error(responseData.error || 'Failed to submit form');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm fixed top-0 left-0 right-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div 
              className="flex items-center cursor-pointer" 
              onClick={() => scrollToSection('hero')}
            >
              <img src={verkkoLogo} alt="Verkko Logo" className="h-12 w-auto" />
            </div>
            <nav className="hidden md:flex space-x-8">
              <button 
                onClick={() => scrollToSection('process')} 
                className="text-body-text hover:text-verkko-blue transition-colors"
              >
                How We Work
              </button>
              <button 
                onClick={() => scrollToSection('services')} 
                className="text-body-text hover:text-verkko-blue transition-colors"
              >
                What We Deliver
              </button>
              <button 
                onClick={() => scrollToSection('about')} 
                className="text-body-text hover:text-verkko-blue transition-colors"
              >
                About
              </button>
            </nav>
            <button onClick={openModal} className="bg-verkko-blue text-white px-4 py-2 rounded-lg hover:opacity-90 transition-all">
              Contact
            </button>
          </div>
        </div>
      </header>

      {/* Add padding to account for fixed header */}
      <div className="pt-20">
        {/* Hero Section */}
        <section id="hero" className="bg-gradient-to-br from-blue-50 to-purple-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-h1 font-medium text-theme-heading mb-6">
                Custom AI Workflows That Handle Your 
                <span className="block gradient-text">Boring, Time-Consuming Tasks</span>
              </h2>
              <p className="text-xl text-body-text mb-8 max-w-4xl mx-auto">
                Stop wasting hours on repetitive work. We design and build intelligent automation that takes care of the mundane stuff, 
                so you can focus on growing your business and making strategic decisions.
              </p>
                          <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={() => scrollToSection('process')} 
                  className="bg-verkko-blue text-white px-8 py-3 rounded-lg text-lg font-medium hover:opacity-90 transition-all"
                >
                  Show Me How
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h3 className="text-h3 font-medium text-theme-heading mb-4">We Don't Just Talk AI - We Build Solutions That Actually Work</h3>
              <p className="text-lg text-body-text max-w-4xl mx-auto">
                While others throw around buzzwords, we listen to your problems and build custom workflows that save you time and money. 
                Every solution starts with understanding your specific challenges, not generic AI promises.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-12">
              <div className="text-center px-4">
                <div className="w-16 h-16 bg-verkko-blue rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="fas fa-clock text-2xl text-white"></i>
                </div>
                <h4 className="text-h5 font-medium text-theme-heading mb-4">Save Hours Every Week</h4>
                <p className="text-body-text leading-relaxed">
                  Eliminate repetitive tasks that eat up your day. Our AI workflows handle the boring stuff automatically, 
                  giving you back time to focus on what actually grows your business.
                </p>
              </div>
              <div className="text-center px-4">
                <div className="w-16 h-16 bg-verkko-purple rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="fas fa-users text-2xl text-white"></i>
                </div>
                <h4 className="text-h5 font-medium text-theme-heading mb-4">Do More With Your Current Team</h4>
                <p className="text-body-text leading-relaxed">
                  You don't need to hire more people to handle growing workloads. Smart automation multiplies your team's capacity 
                  without adding overhead.
                </p>
              </div>
              <div className="text-center px-4">
                <div className="w-16 h-16 bg-verkko-blue rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="fas fa-puzzle-piece text-2xl text-white"></i>
                </div>
                <h4 className="text-h5 font-medium text-theme-heading mb-4">Simple Solutions, Not Tech Complexity</h4>
                <p className="text-body-text leading-relaxed">
                  No confusing jargon or complicated systems. We build workflows that integrate with your existing tools 
                  and are easy for your team to use.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section id="process" className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h3 className="text-h3 font-medium text-theme-heading mb-4">Our Step-by-Step Process</h3>
              <p className="text-lg text-body-text">No mystery process or vague promises. Here's exactly how we turn your time-wasting problems into automated solutions.</p>
            </div>
            
            <div className="relative">
              <div className="absolute left-8 top-16 bottom-16 w-0.5 bg-gradient-to-b from-verkko-blue via-verkko-purple to-verkko-blue opacity-30 hidden md:block"></div>
              
              <div className="space-y-8">
                <div className="group bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 relative">
                  <div className="flex flex-col md:flex-row items-start gap-6">
                    <div className="flex-shrink-0 relative z-10">
                      <div className="w-16 h-16 bg-gradient-to-br from-verkko-blue to-blue-600 rounded-full flex items-center justify-center shadow-lg ring-4 ring-white group-hover:scale-110 group-hover:shadow-xl transition-all duration-300">
                        <div className="flex flex-col items-center">
                          <i className="fas fa-ear-listen text-white text-lg mb-1"></i>
                          <span className="text-white font-bold text-xs">1</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-h5 font-medium text-theme-heading mb-3 group-hover:text-verkko-blue transition-colors duration-300">Listen to Your Problems</h4>
                      <p className="text-body-text leading-relaxed">
                        We sit down and understand exactly what's eating up your time. What tasks do you dread? What processes slow you down? We map out your pain points.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="group bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 relative">
                  <div className="flex flex-col md:flex-row items-start gap-6">
                    <div className="flex-shrink-0 relative z-10">
                      <div className="w-16 h-16 bg-gradient-to-br from-verkko-purple to-purple-600 rounded-full flex items-center justify-center shadow-lg ring-4 ring-white group-hover:scale-110 group-hover:shadow-xl transition-all duration-300">
                        <div className="flex flex-col items-center">
                          <i className="fas fa-drafting-compass text-white text-lg mb-1"></i>
                          <span className="text-white font-bold text-xs">2</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-h5 font-medium text-theme-heading mb-3 group-hover:text-verkko-purple transition-colors duration-300">Design Your Solution</h4>
                      <p className="text-body-text leading-relaxed">
                        We identify which parts of your workflow can be automated and design a custom solution using AI tools and workflow engines.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="group bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 relative">
                  <div className="flex flex-col md:flex-row items-start gap-6">
                    <div className="flex-shrink-0 relative z-10">
                      <div className="w-16 h-16 bg-gradient-to-br from-verkko-blue to-blue-600 rounded-full flex items-center justify-center shadow-lg ring-4 ring-white group-hover:scale-110 group-hover:shadow-xl transition-all duration-300">
                        <div className="flex flex-col items-center">
                          <i className="fas fa-hammer text-white text-lg mb-1"></i>
                          <span className="text-white font-bold text-xs">3</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-h5 font-medium text-theme-heading mb-3 group-hover:text-verkko-blue transition-colors duration-300">Build a Working Prototype</h4>
                      <p className="text-body-text leading-relaxed">
                        Before any big investment, we create a working prototype. You see exactly how it will work with your real data and processes.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="group bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 relative">
                  <div className="flex flex-col md:flex-row items-start gap-6">
                    <div className="flex-shrink-0 relative z-10">
                      <div className="w-16 h-16 bg-gradient-to-br from-verkko-purple to-purple-600 rounded-full flex items-center justify-center shadow-lg ring-4 ring-white group-hover:scale-110 group-hover:shadow-xl transition-all duration-300">
                        <div className="flex flex-col items-center">
                          <i className="fas fa-chart-line text-white text-lg mb-1"></i>
                          <span className="text-white font-bold text-xs">4</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-h5 font-medium text-theme-heading mb-3 group-hover:text-verkko-purple transition-colors duration-300">Test and Prove Value</h4>
                      <p className="text-body-text leading-relaxed">
                        We run the prototype and measure the results. If it doesn't save you significant time and money, we don't move forward.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="group bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 relative">
                  <div className="flex flex-col md:flex-row items-start gap-6">
                    <div className="flex-shrink-0 relative z-10">
                      <div className="w-16 h-16 bg-gradient-to-br from-verkko-blue to-blue-600 rounded-full flex items-center justify-center shadow-lg ring-4 ring-white group-hover:scale-110 group-hover:shadow-xl transition-all duration-300">
                        <div className="flex flex-col items-center">
                          <i className="fas fa-rocket text-white text-lg mb-1"></i>
                          <span className="text-white font-bold text-xs">5</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-h5 font-medium text-theme-heading mb-3 group-hover:text-verkko-blue transition-colors duration-300">Build and Deploy</h4>
                      <p className="text-body-text leading-relaxed">
                        Once proven, we develop the full solution and integrate it seamlessly with your existing tools and processes.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h3 className="text-h3 font-medium text-theme-heading mb-4">What We Deliver</h3>
              <p className="text-lg text-body-text">The end result? Custom solutions designed specifically for your business:</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gray-50 p-8 rounded-xl shadow-sm card-hover">
                <div className="w-12 h-12 bg-verkko-blue rounded-lg flex items-center justify-center mb-4">
                  <i className="fas fa-project-diagram text-white"></i>
                </div>
                <h4 className="text-h5 font-medium text-theme-heading mb-4">Custom Workflow Automation</h4>
                <p className="text-body-text">
                Turn your repetitive tasks into smart, automated processes. From data entry to customer follow-ups, we build workflows
                that handle routine work so you don't have to.
                </p>
              </div>
              
              <div className="bg-gray-50 p-8 rounded-xl shadow-sm card-hover">
                <div className="w-12 h-12 bg-verkko-purple rounded-lg flex items-center justify-center mb-4">
                  <i className="fas fa-search text-white"></i>
                </div>
                <h4 className="text-h5 font-medium text-theme-heading mb-4">Process Analysis & Optimization</h4>
                <p className="text-body-text">
                  We examine your current workflows and identify bottlenecks and time-wasters that can be eliminated or automated.
                </p>
              </div>
              
              <div className="bg-gray-50 p-8 rounded-xl shadow-sm card-hover">
                <div className="w-12 h-12 bg-verkko-blue rounded-lg flex items-center justify-center mb-4">
                  <i className="fas fa-flask text-white"></i>
                </div>
                <h4 className="text-h5 font-medium text-theme-heading mb-4">Prototype Development</h4>
                <p className="text-body-text">
                  See your solution working before you commit. We build working prototypes using low-code/no-code tools 
                  to prove value quickly.
                </p>
              </div>
              
              <div className="bg-gray-50 p-8 rounded-xl shadow-sm card-hover">
                <div className="w-12 h-12 bg-verkko-purple rounded-lg flex items-center justify-center mb-4">
                  <i className="fas fa-tools text-white"></i>
                </div>
                <h4 className="text-h5 font-medium text-theme-heading mb-4">Integration & Training</h4>
                <p className="text-body-text">
                We connect solutions with your existing tools wherever possible. We handle the technical setup and train 
                your team on what they need to know to use the new workflows effectively.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-verkko-blue to-verkko-purple">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h3 className="text-h3 font-medium text-white mb-4">Ready to Get Your Time Back?</h3>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Book a 30-minute call where we'll discuss your biggest time-wasters and show you exactly how automation can solve them. 
              No sales pitch - just practical solutions.
            </p>
                       <button onClick={openModal} className="bg-white text-verkko-blue px-8 py-3 rounded-lg text-lg font-medium hover:bg-gray-100 transition-all">
               Let's Talk About Your Time-Wasters
             </button>
          </div>
        </section>

        {/* Contact Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              {/* Background overlay */}
              <div 
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
                aria-hidden="true"
                onClick={closeModal}
              ></div>

              {/* Modal positioning */}
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

              {/* Modal content */}
              <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-2xl font-medium text-theme-heading" id="modal-title">
                      Ready to Automate Your Workflows?
                    </h3>
                               <p className="text-body-text mt-2">
                       Tell us about your time-wasting tasks and we'll show you how to automate them.
                     </p>
                  </div>
                  <button 
                    onClick={closeModal}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <i className="fas fa-times text-xl"></i>
                  </button>
                </div>

                {submitStatus === 'success' && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center">
                      <i className="fas fa-check-circle text-green-600 mr-3"></i>
                      <p className="text-green-800 font-medium">Thanks for reaching out! We'll get back to you within 24 hours.</p>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center">
                      <i className="fas fa-exclamation-circle text-red-600 mr-3"></i>
                      <p className="text-red-800 font-medium">Sorry, there was an error sending your message. Please try again or email us directly.</p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="modal-name" className="block text-sm font-medium text-gray-700 mb-1">
                        Name *
                      </label>
                      <input
                        type="text"
                        id="modal-name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        maxLength="100"
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-verkko-blue focus:border-transparent transition-all ${formErrors.name ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Your name"
                      />
                      {formErrors.name && <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>}
                    </div>
                    <div>
                      <label htmlFor="modal-email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="modal-email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        maxLength="254"
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-verkko-blue focus:border-transparent transition-all ${formErrors.email ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="your@email.com"
                      />
                      {formErrors.email && <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="modal-company" className="block text-sm font-medium text-gray-700 mb-1">
                        Company
                      </label>
                      <input
                        type="text"
                        id="modal-company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        maxLength="100"
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-verkko-blue focus:border-transparent transition-all ${formErrors.company ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Your company name"
                      />
                      {formErrors.company && <p className="text-red-500 text-sm mt-1">{formErrors.company}</p>}
                    </div>
                    <div>
                      <label htmlFor="modal-phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Phone
                      </label>
                      <input
                        type="tel"
                        id="modal-phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        maxLength="20"
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-verkko-blue focus:border-transparent transition-all ${formErrors.phone ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Your phone number"
                      />
                      {formErrors.phone && <p className="text-red-500 text-sm mt-1">{formErrors.phone}</p>}
                    </div>
                  </div>

                  <div>
                                       <label htmlFor="modal-message" className="block text-sm font-medium text-gray-700 mb-1">
                       What tasks waste your time? *
                     </label>
                     <textarea
                       id="modal-message"
                       name="message"
                       value={formData.message}
                       onChange={handleInputChange}
                       required
                       rows={4}
                       maxLength="1000"
                       className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-verkko-blue focus:border-transparent transition-all resize-none ${formErrors.message ? 'border-red-500' : 'border-gray-300'}`}
                       placeholder="Describe your repetitive tasks or time-consuming processes..."
                     />
                     <div className="flex justify-between items-center mt-1">
                       {formErrors.message && <p className="text-red-500 text-sm">{formErrors.message}</p>}
                       <p className="text-gray-400 text-sm ml-auto">{formData.message.length}/1000</p>
                     </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-gradient-to-r from-verkko-blue to-verkko-purple text-white px-6 py-2 rounded-lg font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center">
                          <i className="fas fa-spinner fa-spin mr-2"></i>
                          Sending...
                        </span>
                      ) : (
                        'Send Message'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <footer id="contact" className="bg-gray-900 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center mb-4">
                  <img src={verkkoLogoWhite} alt="Verkko Logo" className="h-16 w-auto" />
                </div>
                <p className="text-gray-300 mb-6">Custom AI workflows that handle your boring, time-consuming tasks</p>
                {/* <div className="flex space-x-4">
                  <button 
                    className="text-gray-400 hover:text-white transition-colors"
                    onClick={() => window.open('https://twitter.com/verkko', '_blank')}
                    aria-label="Follow us on Twitter"
                  >
                    <i className="fab fa-twitter text-xl"></i>
                  </button>
                  <button 
                    className="text-gray-400 hover:text-white transition-colors"
                    onClick={() => window.open('https://linkedin.com/company/verkko', '_blank')}
                    aria-label="Follow us on LinkedIn"
                  >
                    <i className="fab fa-linkedin text-xl"></i>
                  </button>
                </div> */}
              </div>
              {/* <div className="text-center md:text-right">
                <h5 className="text-xl font-medium mb-4">Let's Chat</h5>
                <a href="mailto:info@verkko.au" className="text-blue-400 hover:text-blue-300 transition-colors text-lg">
                  info@verkko.au
                </a>
              </div> */}
            </div>
            <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
              <p>&copy; 2025 Verkko. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <Routes>
                <Route path="/" element={<MainContent />} />
         <Route path="/about" element={<MainContent />} />
         <Route path="/process" element={<MainContent />} />
         <Route path="/services" element={<MainContent />} />
         <Route path="*" element={<MainContent />} />
       </Routes>
    </Router>
  );
};

export default App; 